/**
 * CollapsiblePane — reusable section-header with collapse toggle.
 *
 * Persists collapsed/expanded state per (pageId, id) to localStorage.
 * Master toggle via <CollapsiblePaneGroup>.
 *
 * Design spec: .claude/design/ui-primitives-collapsible-grouped-table.md
 * Motivates backlog items R018, R033, R038.
 */

import React, { useCallback, useMemo } from 'react';
import { theme } from 'antd';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { useCollapsibleState } from './useCollapsibleState';

/**
 * @param {Object} props
 * @param {string} props.id — required, persistence key within a page.
 * @param {string} props.pageId — required, persistence-key prefix.
 * @param {React.ReactNode} props.title
 * @param {React.ReactNode} [props.summary] — optional right-aligned header content (e.g. counter).
 * @param {React.ReactNode} [props.headerRight] — optional right-side header slot (e.g. <Tag>AI drafted</Tag>).
 * @param {boolean} [props.defaultExpanded=true]
 * @param {(expanded: boolean) => void} [props.onToggle]
 * @param {React.ReactNode} props.children
 */
export default function CollapsiblePane({
    id,
    pageId,
    title,
    summary,
    headerRight,
    defaultExpanded = true,
    onToggle,
    children,
}) {
    if (!id || !pageId) {
        // Loud fail — persistence key would otherwise silently collide across panes.
        throw new Error('CollapsiblePane: id and pageId are required');
    }

    const { token } = theme.useToken();
    const [expanded, setExpanded] = useCollapsibleState(pageId, id, defaultExpanded);

    const handleToggle = useCallback(() => {
        const next = !expanded;
        setExpanded(next);
        if (onToggle) onToggle(next);
    }, [expanded, setExpanded, onToggle]);

    const handleKeyDown = useCallback(
        (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleToggle();
            }
        },
        [handleToggle],
    );

    const styles = useMemo(
        () => ({
            wrap: {
                border: `1px solid ${token.colorBorderSecondary}`,
                borderRadius: token.borderRadius,
                marginBottom: 12,
                background: token.colorBgContainer,
            },
            header: {
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '10px 14px',
                cursor: 'pointer',
                userSelect: 'none',
                borderBottom: expanded ? `1px solid ${token.colorBorderSecondary}` : 'none',
                background: expanded ? token.colorBgLayout : token.colorBgContainer,
                borderTopLeftRadius: token.borderRadius,
                borderTopRightRadius: token.borderRadius,
                borderBottomLeftRadius: expanded ? 0 : token.borderRadius,
                borderBottomRightRadius: expanded ? 0 : token.borderRadius,
            },
            title: {
                fontWeight: 600,
                fontSize: 14,
                flexGrow: 1,
                color: token.colorText,
            },
            summary: {
                color: token.colorTextSecondary,
                fontSize: 12,
                marginRight: 8,
            },
            body: {
                padding: 16,
            },
        }),
        [token, expanded],
    );

    return (
        <div style={styles.wrap} data-collapsible-pane-id={id}>
            <div
                role="button"
                tabIndex={0}
                aria-expanded={expanded}
                aria-controls={`cp-body-${id}`}
                onClick={handleToggle}
                onKeyDown={handleKeyDown}
                style={styles.header}
            >
                {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                <span style={styles.title}>{title}</span>
                {summary && <span style={styles.summary}>{summary}</span>}
                {headerRight}
            </div>
            {expanded && (
                <div id={`cp-body-${id}`} style={styles.body}>
                    {children}
                </div>
            )}
        </div>
    );
}
